// Single source for the toolbar's monochrome icon set. Built with
// createElementNS (not innerHTML) so the SVG lives in the real SVG
// namespace and inherits `currentColor` from the button. Feather /
// Lucide-style stroked geometry on a 16×16 box at stroke-width 1.5 so
// every glyph carries the same visual weight. All path data lives only
// here — consumers ask for an icon by name.

const SVG_NS = 'http://www.w3.org/2000/svg';

export type IconName =
  | 'run'
  | 'reset'
  | 'new'
  | 'save'
  | 'save-as'
  | 'open'
  | 'theme-dark'
  | 'theme-light'
  | 'palette'
  | 'learn'
  | 'close'
  | 'link'
  | 'grid';

interface Child {
  tag: 'path' | 'circle' | 'line' | 'polyline' | 'polygon' | 'rect';
  attrs: Record<string, string | number>;
}

// Geometry is hand-tuned to the 16-unit box. `fill` is overridden to
// `currentColor` only where a glyph reads better filled (the run
// triangle); everything else is stroked.
const ICONS: Record<IconName, Child[]> = {
  // Solid play triangle — the primary action wants weight.
  run: [{ tag: 'polygon', attrs: { points: '5,3.5 12.5,8 5,12.5', fill: 'currentColor', stroke: 'none' } }],
  // Circular arrow (refresh).
  reset: [
    { tag: 'path', attrs: { d: 'M13 8a5 5 0 1 1-1.46-3.54' } },
    { tag: 'polyline', attrs: { points: '13 2.5 13 4.8 10.7 4.8' } },
  ],
  // Plus inside a document.
  new: [
    { tag: 'path', attrs: { d: 'M4 2.2h5l3 3v8.6H4z' } },
    { tag: 'line', attrs: { x1: 8, y1: 7.3, x2: 8, y2: 11.3 } },
    { tag: 'line', attrs: { x1: 6, y1: 9.3, x2: 10, y2: 9.3 } },
  ],
  // Floppy disk.
  save: [
    { tag: 'path', attrs: { d: 'M3 3h8.5L13 4.5V13H3z' } },
    { tag: 'polyline', attrs: { points: '5.5 3 5.5 6 10 6' } },
    { tag: 'rect', attrs: { x: 5.5, y: 8.5, width: 5, height: 4 } },
  ],
  // Floppy disk + a small pen nib (save-as = save under a new name).
  'save-as': [
    { tag: 'path', attrs: { d: 'M3 3h6.5L11 4.5V8' } },
    { tag: 'path', attrs: { d: 'M3 3v10h5.5' } },
    { tag: 'polyline', attrs: { points: '5 3 5 5.5 8.5 5.5' } },
    { tag: 'path', attrs: { d: 'M13.4 8.6 9.7 12.3l-1.8.5.5-1.8 3.7-3.7a.9.9 0 0 1 1.3 1.3z' } },
  ],
  // Open folder.
  open: [
    { tag: 'path', attrs: { d: 'M2 4.5h4l1.2 1.5H14v6.5H2z' } },
    { tag: 'path', attrs: { d: 'M2 7h12' } },
  ],
  // Crescent moon (currently dark → click for light shows the sun;
  // the toolbar swaps which one it shows).
  'theme-dark': [{ tag: 'path', attrs: { d: 'M13 9.5A5.5 5.5 0 0 1 6.5 3a5.5 5.5 0 1 0 6.5 6.5z' } }],
  // Sun with rays.
  'theme-light': [
    { tag: 'circle', attrs: { cx: 8, cy: 8, r: 3 } },
    { tag: 'line', attrs: { x1: 8, y1: 1.5, x2: 8, y2: 3 } },
    { tag: 'line', attrs: { x1: 8, y1: 13, x2: 8, y2: 14.5 } },
    { tag: 'line', attrs: { x1: 1.5, y1: 8, x2: 3, y2: 8 } },
    { tag: 'line', attrs: { x1: 13, y1: 8, x2: 14.5, y2: 8 } },
    { tag: 'line', attrs: { x1: 3.4, y1: 3.4, x2: 4.5, y2: 4.5 } },
    { tag: 'line', attrs: { x1: 11.5, y1: 11.5, x2: 12.6, y2: 12.6 } },
    { tag: 'line', attrs: { x1: 3.4, y1: 12.6, x2: 4.5, y2: 11.5 } },
    { tag: 'line', attrs: { x1: 11.5, y1: 4.5, x2: 12.6, y2: 3.4 } },
  ],
  // Command glyph (⌘) drawn as four loops + a centre square.
  palette: [
    { tag: 'rect', attrs: { x: 6, y: 6, width: 4, height: 4 } },
    { tag: 'path', attrs: { d: 'M6 6V4.2A1.7 1.7 0 1 0 4.3 6z' } },
    { tag: 'path', attrs: { d: 'M10 6h1.7A1.7 1.7 0 1 0 10 4.3z' } },
    { tag: 'path', attrs: { d: 'M10 10v1.7a1.7 1.7 0 1 0 1.7-1.7z' } },
    { tag: 'path', attrs: { d: 'M6 10H4.3A1.7 1.7 0 1 0 6 11.7z' } },
  ],
  // Question mark in a circle (Learn).
  learn: [
    { tag: 'circle', attrs: { cx: 8, cy: 8, r: 6 } },
    { tag: 'path', attrs: { d: 'M6.3 6.2a1.8 1.8 0 0 1 3.5.6c0 1.2-1.8 1.6-1.8 2.8' } },
    { tag: 'line', attrs: { x1: 8, y1: 11.4, x2: 8, y2: 11.5 } },
  ],
  // Plain × — shown on the Learn toggle while the tutorial is open.
  close: [
    { tag: 'line', attrs: { x1: 4, y1: 4, x2: 12, y2: 12 } },
    { tag: 'line', attrs: { x1: 12, y1: 4, x2: 4, y2: 12 } },
  ],
  // Chain link (share) — two interlocking halves over a centre bar.
  link: [
    { tag: 'path', attrs: { d: 'M6.8 9.2a2.5 2.5 0 0 0 3.6.1l2-2a2.5 2.5 0 1 0-3.6-3.6l-1 1' } },
    { tag: 'path', attrs: { d: 'M9.2 6.8a2.5 2.5 0 0 0-3.6-.1l-2 2a2.5 2.5 0 1 0 3.6 3.6l1-1' } },
  ],
  // 3×3 grid (a data file in the tree).
  grid: [
    { tag: 'rect', attrs: { x: 2.5, y: 2.5, width: 11, height: 11 } },
    { tag: 'line', attrs: { x1: 2.5, y1: 6.2, x2: 13.5, y2: 6.2 } },
    { tag: 'line', attrs: { x1: 2.5, y1: 9.8, x2: 13.5, y2: 9.8 } },
    { tag: 'line', attrs: { x1: 6.2, y1: 2.5, x2: 6.2, y2: 13.5 } },
    { tag: 'line', attrs: { x1: 9.8, y1: 2.5, x2: 9.8, y2: 13.5 } },
  ],
};

/// Build a fresh `<svg>` element for `name`. Each call returns a new
/// node (SVG elements can't be shared across mount points).
export function icon(name: IconName): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('width', '16');
  svg.setAttribute('height', '16');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.5');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  for (const child of ICONS[name]) {
    const el = document.createElementNS(SVG_NS, child.tag);
    for (const [k, v] of Object.entries(child.attrs)) {
      el.setAttribute(k, String(v));
    }
    svg.appendChild(el);
  }
  return svg;
}
